<template>
  <div class="pdl-collapse" ref="collapseContainer">
    <!-- @slot Slot for content. -->
    <slot></slot>
  </div>
</template>

<script setup>
import {ref} from 'vue';
import {CHANGE_EVENT, UPDATE_MODEL_EVENT} from './constants';
import {useCollapse} from './useCollapse.js';
defineOptions({
  name: 'PdlCollapse',
});

const props = defineProps({
  /**
   * Enable accordion variant.
   */
  accordion: Boolean,
  /**
   * If false, sets all sections to collapsed state on load.
   */
  defaultActive: {
    type: Boolean,
    default: true,
  },
  /**
   * Sets Currently active panel.
   */
  modelValue: {
    type: [Array, String, Number],
    default() {
      return [];
    },
  },
  /**
   * Sets heading level of the collapse items.
   * This will determine the HTML element used for the heading (e.g., `<h1>`, `<h2>`, etc.).
   * If no level is defined, a `<h3>` tag will be used by default.
   */
  headerLevel: {
    type: [Number, String],
    default: 3,
  },
  /**
   * Custom collapsed state icon.
   */
  iconInactive: {
    type: String,
    default: 'expand_more',
  },
  /**
   * Custom expanded state icon.
   */
  iconActive: {
    type: String,
    default: 'expand_less',
  },
  /**
   * Horizontally aligned position of indicator icon.
   */
  iconPosition: {
    type: String,
    default: 'right',
  },
  /**
   * Vertically aligned position of indicator icon.
   */
  alignIcon: {
    type: String,
    default: 'center',
  },
  /**
   * Custom class for heading container.
   */
  headerClasses: {
    type: String,
    default: '',
  },
  /**
   * Custom class for icons.
   */
  iconClasses: {
    type: String,
    default: '',
  },
  /**
   * Custom class for content container.
   */
  contentClasses: {
    type: String,
    default: '',
  },
  /**
   * Sticky collapse headers stay fixed to the top of the screen while scrolling.
   */
  isSticky: {
    type: Boolean,
    default: false,
  },
  /**
   * Offset top for the sticky headers to account for other sticky content on the page.
   */
  stickyHeaderOffset: {
    type: Number,
    default: 0,
  },
  /**
   * Clicking on a collapse item will scroll that item to the top of the screen.
   */
  isScrollToTop: {
    type: Boolean,
    default: false,
  },
  /**
   * Offset scroll to top to prevent collapse items from becoming hidden under nav/higher z-index elements (number applied as px).
   */
  scrollToTopOffset: {
    type: Number,
    default: 0,
  },

  scrollDuration: {
    type: Number,
    default: 300,
  },
});

const emit = defineEmits({
  [UPDATE_MODEL_EVENT]: (value) => value,
  [CHANGE_EVENT]: (value) => value,
});
const {activeNames, setActiveNames, collapseContainer} = useCollapse(props, emit);
defineExpose({
  /** @description active names */
  activeNames,
  /** @description set active names */
  setActiveNames,
});

const collapseTransitionDuration = ref(`${props.scrollDuration}ms`);
</script>
<style>
.pdl-collapse {
  --collapse-transition-duration: v-bind(collapseTransitionDuration);
  --collapse-outline-focus-visible: 2px solid var(--pdl-tab-active-indicator-color);
}
</style>
